import { useState } from "react";
import { X, NotePencil, MusicNotes, Sparkle, Check } from "@phosphor-icons/react";

/**
 * Modal that lets the user choose what to change in their song:
 * - "lyrics": only the lyrics (returns to lyrics editor)
 * - "audio":  only the music/audio (re-renders the audio with the same lyrics)
 * - "both":   both lyrics and audio
 *
 * Props:
 *   open: bool
 *   onClose(): void
 *   freeLyrics: bool  — whether the user still has the free lyric change available
 *   freeAudio:  bool  — whether the user still has the free audio change available
 *   regenCredits: number — how many paid cambios extra the user has
 *   onChoose(choice: "lyrics"|"audio"|"both"): void
 */
export default function RegenChoiceDialog({ open, onClose, freeLyrics, freeAudio, regenCredits, onChoose }) {
  const [selected, setSelected] = useState("");
  if (!open) return null;

  const Option = ({ value, icon: Icon, title, desc, isFree }) => {
    const active = selected === value;
    return (
      <button
        type="button"
        onClick={() => setSelected(value)}
        data-testid={`regen-option-${value}`}
        className={`w-full text-left p-4 rounded-2xl border transition-all flex items-start gap-3 ${
          active
            ? "bg-[#FFB800]/10 border-[#FFB800]"
            : "bg-white/[0.03] border-white/10 hover:border-white/30"
        }`}
      >
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${active ? "bg-[#FFB800] text-black" : "bg-white/5 text-white/70"}`}>
          {active ? <Check weight="bold" size={18} /> : <Icon weight="fill" size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="font-display font-bold text-white">{title}</p>
            {isFree && (
              <span className="px-2 py-0.5 rounded-full bg-[#B4F34A]/15 border border-[#B4F34A]/30 text-[10px] font-bold text-[#B4F34A]">
                GRATIS
              </span>
            )}
            {!isFree && (
              <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-bold text-white/60">
                $20 MXN
              </span>
            )}
          </div>
          <p className="text-xs text-white/60 leading-relaxed">{desc}</p>
        </div>
      </button>
    );
  };

  const confirm = () => {
    if (!selected) return;
    onChoose(selected);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-4" onClick={onClose}>
      <div
        className="w-full sm:max-w-md bg-[#0a0a0a] border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="regen-choice-dialog"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#FFB800]/15 flex items-center justify-center">
              <Sparkle weight="fill" size={18} className="text-[#FFB800]" />
            </div>
            <div>
              <h3 className="font-bold text-white text-base">¿Qué quieres cambiar?</h3>
              <p className="text-xs text-white/50">Tienes {regenCredits} cambio{regenCredits === 1 ? "" : "s"} extra disponible{regenCredits === 1 ? "" : "s"}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid="regen-dialog-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <Option
            value="lyrics"
            icon={NotePencil}
            title="Solo la letra"
            desc="La IA reescribe la letra desde cero respetando tu historia. La música se queda igual."
            isFree={freeLyrics}
          />
          <Option
            value="audio"
            icon={MusicNotes}
            title="Solo el estilo de música"
            desc="La IA compone una nueva versión del audio con la misma letra. Útil si te gustó la letra pero no cómo sonó."
            isFree={freeAudio}
          />
          <Option
            value="both"
            icon={Sparkle}
            title="Ambos (letra + música)"
            desc="Empezamos de cero: nueva letra Y nuevo audio. Es como crear la canción otra vez (usa 1 cambio de cada tipo)."
            isFree={freeLyrics && freeAudio}
          />
        </div>

        <div className="px-5 pb-6 pt-2 border-t border-white/5 flex gap-3">
          <button
            onClick={onClose}
            className="px-4 py-3 rounded-full bg-white/5 hover:bg-white/10 text-white text-sm font-bold"
            data-testid="regen-dialog-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={confirm}
            disabled={!selected}
            data-testid="regen-dialog-confirm"
            className="flex-1 px-4 py-3 rounded-full bg-[#FFB800] hover:bg-[#FFA300] text-black text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-all"
          >
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
}
